<template>
  <VDropdown
    v-model:shown="isShown"
    :distance="4"
    :skidding="-16"
    :triggers="[]"
    :autoHide="false"
    :handleResize="true"
    :disabled="!showTooltip"
    theme="info-tooltip"
    class="status__wrapper"
    :popperClass="`status__popper ${movedToAnotherTeam ? 'status__moved-team-popper' : ''}`"
    placement="right-start"
    @applyShow="onShowTooltip"
  >
    <div
      class="status"
      :class="[stringId, { 'status_fixed-width': fixedWidth }, { 'is-inactive': isInactive }]"
      @mouseenter="openTooltip"
      @mouseleave="scheduleHide"
    >
      <div class="status__label">
        {{ value.label }}
      </div>
      <icon
        v-if="showTooltip"
        data="@icon/alert-tooltip.svg"
        :fill="true"
        width="var(--icon-16)"
        height="var(--icon-16)"
      />

      <icon
        v-if="showStatusSelected"
        data="@icon/check.svg"
        color="var(--serviceDone--02)"
        width="var(--icon-24)"
        height="var(--icon-24)"
        :fill="true"
        class="status__selected"
      />
    </div>
    <template #popper>
      <template v-if="movedToAnotherTeam">
        <div
          class="status__moved-team-tooltip"
          @mouseenter="cancelHide"
          @mouseleave="scheduleHide"
        >
          <BaseSubHeading
            size="large"
            weight="400"
            color="monochrome-06"
            class="status__moved-team-text"
          >
            Moved to
          </BaseSubHeading>
          <TeamAvatarAndName
            v-if="movedToAnotherTeam"
            :team="movedToAnotherTeam"
            class="status__moved-team-label"
          />
        </div>
      </template>
      <template v-else>
        <div
          class="status__description__wrapper"
          @mouseenter="cancelHide"
          @mouseleave="scheduleHide"
          @click.capture="onDescriptionClick"
        >
          <EditorInput
            :id="`statusDescription_${itemId}`"
            :value="description"
            :customExtensions="customExtensions"
            class="status__description"
            :readonly="true"
          />
          <button
            v-tooltip.top="{ content: 'Open comment', theme: 'info-tooltip' }"
            type="button"
            class="status__open-in-new-tab"
            :class="{ 'is-hide': !showLink }"
            @click="$emit('openInNewTab', commentId)"
          >
            <icon
              data="@icon/openInNewTab.svg"
              :fill="true"
              width="var(--icon-16)"
              height="var(--icon-16)"
              color="var(--monochrome05to04)"
            />
          </button>
        </div>
        <BaseSubHeading
          v-if="showAttachmentsCount"
          size="small"
          weight="400"
          color="monochrome-05"
          darkColor="monochrome-04"
          class="status__attachments"
          @mouseenter="cancelHide"
          @mouseleave="scheduleHide"
        >
          {{ attachmentsLabel }}
        </BaseSubHeading>
        <BaseCaption
          class="regular-comment-author status__author"
          size="large"
          color="monochrome-05"
          darkColor="monochrome-04"
          style="margin-bottom: 0; text-align: end"
          @mouseenter="cancelHide"
          @mouseleave="scheduleHide"
        >
          {{ initiator?.firstName }} {{ initiator?.lastName }}
        </BaseCaption>
      </template>
    </template>
  </VDropdown>
</template>

<script setup lang="ts">
import { hideAllPoppers } from "floating-vue"
import { computed, nextTick, onUnmounted, ref, toRefs } from "vue"

import EditorInput from "@/components/Editor/EditorInput.vue"
import TeamAvatarAndName from "@/components/Team/TeamAvatarAndName.vue"

import {
  ChangeItemByUser,
  ItemStatus,
  listOfStatusesWithMessage,
  reviewStatuses,
  Team
} from "@/models"
import { Status } from "@/models/Status"
import { ReferenceAllUser } from "@/plugins/extensions/reference/referenceAt"
import { ReferenceHashFileLink } from "@/plugins/extensions/reference/referenceHash"

const props = withDefaults(
  defineProps<{
    value: Status
    initiator?: ChangeItemByUser
    itemId?: string
    fixedWidth?: boolean
    isInactive?: boolean
    description?: string
    showStatusSelected?: boolean
    commentId?: string
    movedToAnotherTeam?: Team | null
    attachmentsCount?: number
  }>(),
  {
    initiator: () => ({}) as ChangeItemByUser,
    itemId: "",
    fixedWidth: false,
    isInactive: false,
    description: "",
    showStatusSelected: false,
    commentId: "",
    movedToAnotherTeam: null,
    attachmentsCount: 0
  }
)

const {
  value,
  initiator,
  itemId,
  fixedWidth,
  isInactive,
  description,
  showStatusSelected,
  commentId,
  movedToAnotherTeam,
  attachmentsCount
} = toRefs(props)

defineEmits<{
  (event: "openInNewTab", commentId: string): void
}>()

const showLink = ref(false)
const isShown = ref(false)
let hideTimer: ReturnType<typeof setTimeout> | null = null

const customExtensions = computed(() => {
  return itemId.value ? [ReferenceAllUser, ReferenceHashFileLink] : []
})

const stringId = computed(() => `is_${value.value.id.toLowerCase()}`)

const showDescription = computed(() => {
  return description.value && listOfStatusesWithMessage.includes(value.value.id as ItemStatus)
})

const showTooltip = computed(() => {
  return showDescription.value || movedToAnotherTeam.value
})

const showAttachmentsCount = computed(() => {
  return (
    attachmentsCount.value > 0 && listOfStatusesWithMessage.includes(value.value.id as ItemStatus)
  )
})

const attachmentsLabel = computed(() => {
  return `${attachmentsCount.value} Attachment${attachmentsCount.value > 1 ? "(s)" : ""}`
})

async function onShowTooltip() {
  if (movedToAnotherTeam.value) return
  await nextTick()
  checkShowLink()
}

function cancelHide() {
  if (hideTimer) {
    clearTimeout(hideTimer)
    hideTimer = null
  }
}

function scheduleHide() {
  cancelHide()
  hideTimer = setTimeout(() => {
    isShown.value = false
  }, 1000)
}

function openTooltip() {
  if (!showTooltip.value) return
  cancelHide()
  // hideAllPoppers would also hide this open tooltip while isShown stays true, so it never reopens.
  if (isShown.value) return
  hideAllPoppers()
  isShown.value = true
}

function onDescriptionClick(event: MouseEvent) {
  const target = event.target
  if (!(target instanceof Element) || !target.closest(".reference-component__file-link")) return

  cancelHide()
  isShown.value = false
  hideAllPoppers()
}

onUnmounted(() => {
  cancelHide()
})

function checkShowLink() {
  const statusDescription = document.getElementById(
    `statusDescription_${itemId.value}`
  ) as HTMLElement | null
  if (!statusDescription) return
  showLink.value = statusDescription.offsetHeight < statusDescription.scrollHeight
}
</script>

<style scoped lang="scss">
.status__open-in-new-tab:hover :deep(svg path) {
  fill: var(--monochrome-06);
}

.status__attachments {
  margin-top: var(--step2);
}

.status__author {
  margin-top: var(--step2);
}
</style>
